import { Button, Grid, Stack, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { BadgeType, MeetingInfoBadge } from "../MeetingInfoBadge";
import { DetailField } from "./DetailField";
import React from "react";
import { InstanceMetadataEditDialog } from "../metadata_editing/InstanceMetadataEditDialog";
import { HostedMeetingFallbackReadOnly } from "./HostedMeetingFallbackReadOnly";
import { PartnerAssociationReadOnly } from "./PartnerAssociationReadOnly";
import {
  descriptionToString,
  getDateString,
  getTimeString,
  hasApparentOverride,
} from "../../helpers";
import { Meeting } from "../../../../model/meeting";
import { useBoundStore } from "../../../../store/store";

interface InstanceDetailsViewProps {
  meeting: Meeting;
}

export const toInstanceDetail = (meeting: Meeting) => {
  const { recurringMeeting } = meeting;

  const meetingTypeId = meeting.metadata.meetingType;

  const host = meeting.host;
  const missingData = !meetingTypeId || !host;
  const visible = meeting.metadata.memberVisible;
  const differentFromRecurring = recurringMeeting
    ? hasApparentOverride(meeting)
    : undefined;

  return {
    title: meeting.title,
    meetingTypeId,
    date: getDateString(meeting.start),
    time: getTimeString(meeting.start, meeting.end),
    description: descriptionToString(meeting.description),
    isRecurring: recurringMeeting ? true : false,
    hostName: host?.displayName,
    isVisible: visible ?? false,
    isMissingData: missingData,
    isDifferentFromRecurring: differentFromRecurring,
    // Instance override wins; otherwise the key set on the series applies.
    roomKey:
      meeting.metadata.continuousRoomKey ||
      recurringMeeting?.metadata.continuousRoomKey,
  };
};

export const InstanceDetailsView: React.FC<InstanceDetailsViewProps> = ({
  meeting,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [fallbackRefreshKey, setFallbackRefreshKey] = React.useState(0);
  const getMeetingType = useBoundStore((state) => state.getTypeById);

  const {
    title,
    date,
    time,
    meetingTypeId,
    hostName,
    isMissingData,
    isVisible,
    description,
    roomKey,
  } = toInstanceDetail(meeting);
  const meetingTypeInfo = meetingTypeId ? getMeetingType(meetingTypeId) : undefined;
  const meetingType = meetingTypeInfo?.title;
  const airtableDescription = meetingTypeInfo?.description;

  function handleDialogClose() {
    setIsEditing(false);
    setFallbackRefreshKey((k) => k + 1);
  }

  return (
    <Stack gap={2} padding={{ xs: 0, sm: "10px 24px" }}>
      <Stack
        direction="row"
        gap={2}
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Stack gap={1} sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight="bold">
            {title}
          </Typography>
          {(!isVisible || isMissingData) && (
            <Stack direction="row" gap={1} flexWrap="wrap">
              {!isVisible && <MeetingInfoBadge type={BadgeType.notVisible} />}
              {isMissingData && (
                <MeetingInfoBadge type={BadgeType.missingData} />
              )}
            </Stack>
          )}
        </Stack>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => setIsEditing(true)}
          sx={{ flexShrink: 0 }}
        >
          Edit Instance
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <DetailField field="id" value={meeting.id} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DetailField field="date" value={date} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DetailField field="time" value={time} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DetailField field="hostName" value={hostName} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DetailField field="meetingType" value={meetingType} />
        </Grid>
        {roomKey && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <DetailField field="continuousRoomKey" value={roomKey} />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <DetailField
            field="description"
            value={airtableDescription || description}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <HostedMeetingFallbackReadOnly
            gcalEventId={meeting.id}
            refreshKey={fallbackRefreshKey}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <PartnerAssociationReadOnly
            gcalEventId={meeting.id}
            recurringMeetingId={meeting.recurringMeeting?.id}
            refreshKey={fallbackRefreshKey}
          />
        </Grid>
      </Grid>

      <InstanceMetadataEditDialog
        meeting={meeting}
        open={isEditing}
        onClose={handleDialogClose}
      />
    </Stack>
  );
};
